/*
 * Currency, date and time formatters. Pure functions, fully tested.
 * Locale rules: see docs/design/design-system.md §3 — Ukrainian
 * decimal comma, narrow non-breaking space thousands separator,
 * trailing currency symbol with NBSP, DD.MM.YYYY / DD.MM date format.
 */

import type { Currency } from "@/api/types";

const NBSP = " ";
const NARROW_NBSP = " ";

const SYMBOLS: Record<Currency, string> = {
  UAH: "₴",
  EUR: "€",
  USD: "$",
  PLN: "zł",
};

const SR_NAMES: Record<Currency, [singular: string, plural: string, manyPlural: string]> = {
  UAH: ["гривня", "гривні", "гривень"],
  EUR: ["євро", "євро", "євро"],
  USD: ["долар", "долари", "доларів"],
  PLN: ["злотий", "злотих", "злотих"],
};

/** Renders an amount with Ukrainian conventions. e.g. 1240.5 UAH → "1 240,50 ₴" */
export function formatMoney(value: number, ccy: Currency): string {
  const abs = Math.abs(value);
  const fixed = abs.toFixed(2);
  const [intPart, decPart] = fixed.split(".");
  const withThousands = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, NARROW_NBSP);
  return `${withThousands},${decPart}${NBSP}${SYMBOLS[ccy]}`;
}

/** Plain-language SR string, e.g. "240 гривень 50 копійок". */
export function formatMoneySR(value: number, ccy: Currency): string {
  const abs = Math.abs(value);
  const ints = Math.floor(abs);
  const cents = Math.round((abs - ints) * 100);
  const [one, few, many] = SR_NAMES[ccy];
  const intWord = pluralUA(ints, one, few, many);
  if (cents === 0) {
    return `${ints} ${intWord}`;
  }
  return `${ints} ${intWord} ${cents} копійок`;
}

/** Returns DD.MM.YYYY (or DD.MM for the current year if `compact`). */
export function formatDate(iso: string, opts: { compact?: boolean } = {}): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  if (opts.compact && d.getFullYear() === new Date().getFullYear()) {
    return `${dd}.${mm}`;
  }
  const yy = String(d.getFullYear() % 100).padStart(2, "0");
  return `${dd}.${mm}.${yy}`;
}

/** Date label fallback when vendor_name is null. Receipt-card shorthand "Чек DD.MM". */
export function receiptFallbackLabel(iso: string): string {
  return `Чек ${formatDate(iso, { compact: true })}`;
}

/**
 * Pure relative-time formatter implementing the thresholds in
 * docs/design/components.md §3.3. `nowMs` is injectable for tests.
 */
export interface RelativeTimeFormatted {
  text: string;
  /** True when we've passed the 7-day threshold and switched to absolute date. */
  isAbsolute: boolean;
}

export function formatRelativeTime(
  isoValue: string,
  options: { compact?: boolean; nowMs?: number } = {},
): RelativeTimeFormatted {
  const now = options.nowMs ?? Date.now();
  const then = new Date(isoValue).getTime();
  if (Number.isNaN(then)) return { text: "", isAbsolute: false };
  const deltaSec = Math.max(0, Math.floor((now - then) / 1000));
  const compact = !!options.compact;

  if (deltaSec < 60) {
    return { text: "щойно", isAbsolute: false };
  }
  if (deltaSec < 3600) {
    const m = Math.floor(deltaSec / 60);
    return { text: compact ? `${m}хв` : `${m} хв тому`, isAbsolute: false };
  }
  if (deltaSec < 86400) {
    const h = Math.floor(deltaSec / 3600);
    return { text: compact ? `${h}г` : `${h} год тому`, isAbsolute: false };
  }
  if (deltaSec < 7 * 86400) {
    const d = Math.floor(deltaSec / 86400);
    return { text: compact ? `${d}дн` : `${d} дн тому`, isAbsolute: false };
  }
  return {
    text: formatDate(isoValue, { compact: true }),
    isAbsolute: true,
  };
}

/** Ukrainian-style plural picker (1, 2-4, 5+). */
function pluralUA(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** "1 чек" / "2 чеки" / "5 чеків" */
export function pluralReceipts(n: number): string {
  return `${n} ${pluralUA(n, "чек", "чеки", "чеків")}`;
}

export function pluralItems(n: number): string {
  return `${n} ${pluralUA(n, "позиція", "позиції", "позицій")}`;
}

export function pluralParticipants(n: number): string {
  return `${n} ${pluralUA(n, "учасник", "учасники", "учасників")}`;
}

export function pluralTransfers(n: number): string {
  return `${n} ${pluralUA(n, "переказ", "перекази", "переказів")}`;
}
